import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { SignatureRequestDocumentBlock } from '@/features/generation/SignatureRequestBlocks';
import {
  motivoDaTravaDaAssinatura,
  PERMISSAO_BAIXAR_ORIGINAL,
  situacaoDoPedido,
} from '@/features/generation/signatureRequestLabels';
import { enderecoDeArquivo } from '@/shared/api/client';
import { policies, type Policy } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { useToast } from '@/shared/ui/useToast';

import {
  motivoSemDocumento,
  PERMISSAO_GERAR_DOCUMENTO,
  temDocumentoDaApolice,
} from './documentoDaApolice';

/**
 * Documento da apolice (P-MIN-2): gerado na emissao do modelo vinculado em Templates >
 * Vinculos, no servidor, com o que a cotacao e a apolice gravaram. Ver
 * docs/ADR/0057-documento-da-apolice.md.
 *
 * Sem modelo vinculado, a apolice e emitida como antes e o documento fica "nao
 * gerado", com o motivo. O grupo judicial so emite com o modelo vinculado e a prova
 * aprovada: a apolice dele sempre tem documento.
 */

const rotuloDaSituacao: Record<string, string> = {
  gerado: 'Gerado',
  nao_gerado: 'Nao gerado',
  sem_registro: 'Sem registro',
};

/** Abre o endereco temporario em outra aba: quem baixa continua na tela. */
function abrir(url: string) {
  window.open(enderecoDeArquivo(url), '_blank', 'noopener,noreferrer');
}

/**
 * Botao "Baixar apolice": o PDF quando a instalacao converte, o Word quando nao. Usado
 * no painel Emissao da operacao e no tomador.
 */
export function BaixarApoliceButton({
  policy,
  size = 'sm',
}: {
  policy: Policy;
  size?: 'sm' | 'md';
}) {
  const toast = useToast();
  const baixar = useMutation({
    mutationFn: () => policies.document(policy.id),
    onSuccess: (link) => abrir(link.url),
    onError: (error: Error) => toast.error(error.message),
  });
  const motivo = motivoSemDocumento(policy);
  // Com a versao assinada, o botao diz que entrega a assinada (P-MIN-4, MIN-027).
  const assinada = policy.policy_document?.signed === true;
  return (
    <Button
      size={size}
      variant="secondary"
      disabled={motivo !== undefined}
      title={motivo}
      loading={baixar.isPending}
      onClick={() => baixar.mutate()}
    >
      {assinada ? 'Baixar apolice assinada' : 'Baixar apolice'}
    </Button>
  );
}

/** Quadro "Documento da apolice" do detalhe da apolice. */
export function PolicyDocumentCard({ policy }: { policy: Policy }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const estado = policy.policy_document;

  const baixar = useMutation({
    mutationFn: (formato?: 'pdf' | 'docx') => policies.document(policy.id, formato),
    onSuccess: (link) => abrir(link.url),
    onError: (error: Error) => toast.error(error.message),
  });

  // A versao original, depois da assinada, e de quem tem a permissao (P-MIN-4).
  const baixarOriginal = useMutation({
    mutationFn: () => policies.document(policy.id, undefined, 'original'),
    onSuccess: (link) => abrir(link.url),
    onError: (error: Error) => toast.error(error.message),
  });
  const assinada = estado?.signed === true;
  const podeOriginal = can(PERMISSAO_BAIXAR_ORIGINAL);

  const gerar = useMutation({
    mutationFn: () => policies.regenerateDocument(policy.id),
    onSuccess: (apolice) => {
      if (apolice.policy_document?.status === 'nao_gerado') {
        toast.error(
          `O documento nao foi gerado: ${apolice.policy_document.reason ?? 'sem motivo informado.'}`,
        );
      } else {
        toast.success('Documento da apolice gerado de novo.');
      }
      void queryClient.invalidateQueries({ queryKey: ['policy', policy.id] });
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const podeGerar =
    can(PERMISSAO_GERAR_DOCUMENTO) &&
    policy.status !== 'cancelada' &&
    policy.document_signature_status !== 'assinada';
  // Com pedido de assinatura pendente ou assinado sobre o documento vigente, o servidor
  // recusa gerar de novo: o botao fica desligado com o motivo (ADR 0079, R5-A03).
  const travaDaAssinatura = motivoDaTravaDaAssinatura(estado?.signature_status);
  const tem = temDocumentoDaApolice(policy);

  return (
    <DetailCard
      title="Documento da apolice"
      description="Gerado no servidor, do modelo vinculado em Templates > Vinculos, com o que a cotacao e a apolice gravaram."
      action={
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={!tem}
            title={motivoSemDocumento(policy)}
            loading={baixar.isPending && baixar.variables === undefined}
            onClick={() => baixar.mutate(undefined)}
          >
            {assinada ? 'Baixar apolice assinada' : 'Baixar apolice'}
          </Button>
          {assinada && podeOriginal ? (
            <Button
              size="sm"
              variant="secondary"
              loading={baixarOriginal.isPending}
              onClick={() => baixarOriginal.mutate()}
            >
              Baixar original
            </Button>
          ) : null}
          {estado?.status === 'gerado' && (!assinada || podeOriginal) ? (
            <Button
              size="sm"
              variant="secondary"
              loading={baixar.isPending && baixar.variables === 'docx'}
              onClick={() => baixar.mutate('docx')}
            >
              Baixar em Word
            </Button>
          ) : null}
          {podeGerar ? (
            <Button
              size="sm"
              loading={gerar.isPending}
              disabled={travaDaAssinatura !== undefined}
              title={travaDaAssinatura}
              onClick={() => gerar.mutate()}
            >
              Gerar de novo
            </Button>
          ) : null}
        </div>
      }
    >
      <dl className="grid gap-4 sm:grid-cols-3">
        <Dado rotulo="Situacao do documento">
          {estado ? (rotuloDaSituacao[estado.status] ?? estado.status) : 'Sem registro'}
        </Dado>
        <Dado rotulo="Modelo">
          {estado?.template_name
            ? `${estado.template_name}${
                estado.template_version !== null
                  ? ` (versao ${estado.template_version})`
                  : ''
              }`
            : 'Nao definido'}
        </Dado>
        <Dado rotulo="Gerado em">
          {estado?.generated_at ? formatDateTime(estado.generated_at) : 'Nao definido'}
        </Dado>
        <Dado rotulo="Filial da seguradora">
          {policy.insurer_branch?.name ?? 'Nao definido'}
        </Dado>
        <Dado rotulo="Grupo travado">
          {estado?.locked_group
            ? `Sim (${estado.credit_group ?? 'grupo de credito'})`
            : 'Nao'}
        </Dado>
        <Dado rotulo="Formato">
          {estado?.pdf_available ? 'PDF e Word' : 'Word (sem conversor de PDF)'}
        </Dado>
        <Dado rotulo="Assinatura">
          {assinada
            ? `Assinado${estado?.signed_at ? ` em ${formatDateTime(estado.signed_at)}` : ''}`
            : estado?.signature_status
              ? situacaoDoPedido[estado.signature_status].label
              : 'Sem pedido de assinatura'}
        </Dado>
      </dl>
      {estado?.status === 'gerado' && estado.document_id ? (
        <SignatureRequestDocumentBlock
          documentId={estado.document_id}
          subject={`Apolice ${policy.external_number || policy.number}`}
          signed={assinada}
        />
      ) : null}
      {podeGerar && travaDaAssinatura ? (
        <p className="text-xs text-ink-500">{travaDaAssinatura}</p>
      ) : null}
      {estado?.status === 'nao_gerado' && estado.reason ? (
        <Alert tone="warning" title="Documento nao gerado">
          {estado.reason}
        </Alert>
      ) : null}
      {estado?.status === 'sem_registro' && !policy.document ? (
        <p className="text-xs text-ink-500">
          A apolice foi emitida antes do documento da apolice. Quem tem a permissao pode
          gerar o documento agora, do modelo vinculado.
        </p>
      ) : null}
      {(estado?.warnings ?? []).length > 0 ? (
        <Alert tone="info" title="Avisos da geracao">
          {(estado?.warnings ?? []).join(' ')}
        </Alert>
      ) : null}
    </DetailCard>
  );
}

function Dado({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {rotulo}
      </dt>
      <dd className="mt-0.5 text-sm font-medium break-words text-ink-900">
        {children}
      </dd>
    </div>
  );
}
